import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COUNTRY } from '@mango-engineering/maknet-client-sdk/lib/shop/types';
import { ChevronRightSmIcon, SearchTopPicksIconNew, ThumbsUpIcon } from '../../../assets/icons';
import { config } from '../../../config/env';
import { useStaticData } from '../../../contexts/DataProvider.context';
import { useCarouselStyleConfig } from '../../../hooks/useCarouselStyleConfig';
import SendBrowserEvent, { isReactNativeWebView } from '../../../services/sendBrowserEvent.service';
import { CarouselClickEventProps, CarouselPDPClickEventProps } from '../../../types/analytics.types';
import { CarouselSettingProps, RecommendationBanner, SEARCH_PERFORM_TRIGGER_TYPE } from '../../../types/common';
import { Product } from '../../../types/product.types';
import { getFeatureMinVersion, isFeatureEnabled } from '../../../utils/common.util';
import {
  ANALYTICS_CHANNELS,
  BANNER_EVENTS,
  BANNER_TYPES,
  CAROUSEL_TYPES,
  DEFAULT_RECOMMENDATION_SEE_ALL_THRESHOLD,
  FALLBACK_APP_VERSION,
  ProductCardVariant,
  SOURCE_EVENTS,
  SOURCE_EVENT_MAP,
} from '../../../utils/constants';
import { getSingleProductAnalytics, hasMoreProduct, isFlagEnabledForCountry } from '../../../utils/helper';
import { getProductVideoUrl } from '../../../utils/product.util';
import { CarouselHeaderProps } from '../../carouselHeader/component';
import ProductCard from '../../ProductCard';
import ProductCarousel from '../../productCarousel/component';
import ProductListCarousel from '../../productList/component';
import ProducHorizontaltListCarousel from '../../productListTwoRowHorizontal/component';
import ProductCardSkeletonV3 from '../../productSkeleton/component/ProductCardSkeletonV3';
import { SkeletonCard } from '../../productSkeleton/component/SkeletonCard';
import RecommendationBannerCarousel, {
  RECOMMENDATION_BANNER_SLIDE_TYPE,
} from '../../recommendationBannerCarousel/component';
import { SeeAllCard } from '../../SeeAllCard/component';
import {
  BottomSheetBackdrop,
  CarouselContainer,
  ProductContainer,
  SeeAllButton,
  StyledAdBanner,
  TitleContainer,
  TitleText,
} from '../style';

export type CarouselHandle = {
  scrollToIndex: (index: number) => void;
};

const PAGINATION_IFRAME_HEIGHT_THRESHOLD = 80;

export const Carousels: React.FC<CarouselSettingProps> = (carouselSetting) => {
  const { t, i18n } = useTranslation();

  const { carouselModel, userDetails, appMode, countryCode, isNative, appVersion } = useStaticData();
  const {
    title,
    titleIcon,
    data,
    isDiversify,
    recommendationType,
    productCardVariant,
    query,
    platform: platformSetting,
    carouselMaxItemsCount,
    fetchMoreData,
    shouldFetch,
    isHorizonProductCardEnabled,
    isPaginationLoading,
  } = carouselSetting;

  const isLoggedIn = userDetails?.token && userDetails?.userId;
  const scrollRef = useRef<CarouselHandle | null>(null);
  const [isAskToLoginOpen, setIsAskToLoginOpen] = useState(false);

  const isFromBasket = recommendationType === CAROUSEL_TYPES.BASKET_RECOMMENDATION;
  const isBuyAgain = recommendationType === CAROUSEL_TYPES.BUY_AGAIN;

  const containerRef = useRef<HTMLDivElement>(null);
  const hasProductMore = hasMoreProduct(data?.totalCount, data?.products?.length);

  const prevProductsRef = useRef<Product[]>([]);
  const prevPaginationProductCountRef = useRef(0);

  const showSeeAllCount =
    (isReactNativeWebView() ? carouselMaxItemsCount?.mobile : carouselMaxItemsCount?.web) ??
    DEFAULT_RECOMMENDATION_SEE_ALL_THRESHOLD;
  const showSeeAll = showSeeAllCount && data && data?.totalCount > showSeeAllCount;

  const isHorizonUiUxEnabled = useMemo(() => {
    const horizonUiUxMinVersion = getFeatureMinVersion(countryCode, config.HORIZON_UI_UX_MIN_VERSION);
    return isHorizonProductCardEnabled && isFeatureEnabled(horizonUiUxMinVersion, appVersion || FALLBACK_APP_VERSION);
  }, [appVersion, countryCode, isHorizonProductCardEnabled]);

  const productCardVideoMinAppVersion = getFeatureMinVersion(
    countryCode,
    config.SHOPPING_ENABLE_PRODUCT_CARD_VIDEO_MIN_VERSION,
  );
  const isVideoEnabled = useMemo(
    () =>
      Boolean(
        productCardVideoMinAppVersion &&
          isFlagEnabledForCountry(countryCode, config.SHOPPING_ENABLE_PRODUCT_CARD_VIDEO) &&
          isFeatureEnabled(productCardVideoMinAppVersion, appVersion || FALLBACK_APP_VERSION),
      ),
    [appVersion, countryCode, productCardVideoMinAppVersion],
  );

  const firstVideoProductIndex = useMemo(
    () =>
      isVideoEnabled
        ? (data?.products?.findIndex((product) => Boolean(getProductVideoUrl(product)?.trim())) ?? -1)
        : -1,
    [data?.products, isVideoEnabled],
  );

  const isRecommendationBannersEnabled = config.ENABLE_RECOMMENDATION_BANNERS && isHorizonUiUxEnabled;
  const isTwoRowHorizontalScroll = config.ENABLE_PDP_RECOMMENDATION_NEW_UI_FLAG && isHorizonUiUxEnabled;

  const recommendationBanners = useMemo(() => {
    const isHomeRecommendation =
      recommendationType === CAROUSEL_TYPES.HOME_RECOMMENDATIONS || recommendationType === CAROUSEL_TYPES.HOME;

    if (!isRecommendationBannersEnabled || !isHomeRecommendation) {
      return [];
    }

    return carouselModel.bannerData ?? [];
  }, [carouselModel.bannerData, isRecommendationBannersEnabled, recommendationType]);

  const showVerticalList = isHorizonUiUxEnabled;

  const reportCarouselHeight = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const heights = [
      el.scrollHeight,
      el.offsetHeight,
      el.getBoundingClientRect().height,
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
    ].filter((value) => value > 0);

    if (!heights.length) return;

    const height = Math.ceil(Math.max(...heights));

    if (isFromBasket && !showVerticalList) {
      SendBrowserEvent.horizonPaginationHeight(height, true);
      return;
    }

    SendBrowserEvent.heightChange(height, isFromBasket, showVerticalList);
  }, [isFromBasket, showVerticalList]);

  const getCarouselHeight = useCallback(() => {
    const el = containerRef.current;
    if (!el) return 0;

    return Math.max(el.scrollHeight, el.offsetHeight, el.getBoundingClientRect().height);
  }, []);

  const sendHorizonPaginationHeight = useCallback(() => {
    if (!isHorizonUiUxEnabled) return;

    const height = getCarouselHeight();
    if (!height) return;

    SendBrowserEvent.horizonPaginationHeight(height + PAGINATION_IFRAME_HEIGHT_THRESHOLD, isFromBasket);
  }, [getCarouselHeight, isFromBasket, isHorizonUiUxEnabled]);

  const scheduleHorizonPaginationHeight = useCallback(() => {
    if (!isHorizonUiUxEnabled) return;

    window.requestAnimationFrame(sendHorizonPaginationHeight);
    [100, 300, 600].forEach((delay) => {
      window.setTimeout(sendHorizonPaginationHeight, delay);
    });
  }, [isHorizonUiUxEnabled, sendHorizonPaginationHeight]);

  const handleAskToLoginOpen = useCallback(() => {
    setIsAskToLoginOpen(true);
  }, []);

  const handleAskToLoginClose = useCallback(() => {
    setIsAskToLoginOpen(false);
    scheduleHorizonPaginationHeight();
  }, [scheduleHorizonPaginationHeight]);

  const platform = platformSetting || (isReactNativeWebView() ? 'app' : 'web');

  const styleConfig = useCarouselStyleConfig({
    carouselType: recommendationType ?? '',
    platform: platform,
    productCardVariant: productCardVariant ?? ProductCardVariant.NORMAL,
  });

  const analyticProperties = useMemo<CarouselClickEventProps>(
    () => ({
      isDiversifyResult: isDiversify,
      algorithm: data?.algorithm ?? '',
      getPersonalizedRecommendationsFromDb: data?.recommendationType ?? '',
      carouselName: title,
      sourceEvent: SOURCE_EVENT_MAP[recommendationType ?? ''] || SOURCE_EVENTS.HOME_PAGE_TOP_PICKS_RECOMMENDATIONS,
    }),
    [isDiversify, data?.algorithm, data?.recommendationType, title, recommendationType],
  );

  const seeAll = useCallback(
    (triggerType: string) => {
      const sourceEvent =
        SOURCE_EVENT_MAP[recommendationType ?? ''] || SOURCE_EVENTS.HOME_PAGE_TOP_PICKS_RECOMMENDATIONS;

      const message = {
        ...analyticProperties,
        isFrombasket: isFromBasket,
        title,
        sourceEvent,
        triggerType,
        type: recommendationType,
        appMode,
      };

      SendBrowserEvent.triggerSeeAllAction(message, isFromBasket);
    },
    [recommendationType, title, analyticProperties, isFromBasket, appMode],
  );

  useEffect(() => {
    const prev = prevProductsRef.current;
    const next = data?.products;
    if (next?.length && (prev.length !== next.length || prev[prev.length - 1]?.id !== next[next.length - 1]?.id)) {
      prevProductsRef.current = next;
      SendBrowserEvent.sendIntialProducts(data!, isFromBasket);
    }
  }, [data, isFromBasket]);

  useEffect(() => {
    if (data?.products?.length) {
      SendBrowserEvent.sendProductCount(data?.products?.length ?? 0, isFromBasket);
    }
  }, [data, isFromBasket]);

  useEffect(() => {
    if (!showVerticalList) return;

    document.documentElement.classList.add('vertical-carousel');
    document.body.classList.add('vertical-carousel');
    document.getElementById('root')?.classList.add('vertical-carousel');
    if (isHorizonUiUxEnabled) {
      document.documentElement.classList.add('horizon-carousel');
      document.body.classList.add('horizon-carousel');
      document.getElementById('root')?.classList.add('horizon-carousel');
    }
    SendBrowserEvent.setIframeHeight(isHorizonUiUxEnabled, false);

    return () => {
      document.documentElement.classList.remove('vertical-carousel');
      document.body.classList.remove('vertical-carousel');
      document.getElementById('root')?.classList.remove('vertical-carousel');
      document.documentElement.classList.remove('horizon-carousel');
      document.body.classList.remove('horizon-carousel');
      document.getElementById('root')?.classList.remove('horizon-carousel');
    };
  }, [showVerticalList, isHorizonUiUxEnabled]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      reportCarouselHeight();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [reportCarouselHeight]);

  useEffect(() => {
    if (!showVerticalList && !isFromBasket) return;

    reportCarouselHeight();
    const timers = [100, 300, 600, 1000].map((delay) => window.setTimeout(reportCarouselHeight, delay));

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [data?.products?.length, isFromBasket, showVerticalList, reportCarouselHeight]);

  useEffect(() => {
    if (!isHorizonUiUxEnabled || !data?.products?.length) return;

    const currentProductCount = data.products.length;
    const previousProductCount = prevPaginationProductCountRef.current;
    prevPaginationProductCountRef.current = currentProductCount;

    if (!previousProductCount || currentProductCount <= previousProductCount) return;

    const animationFrameId = window.requestAnimationFrame(() => sendHorizonPaginationHeight());
    const timers = [100, 300].map((delay) => window.setTimeout(() => sendHorizonPaginationHeight(), delay));

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [data?.products, isHorizonUiUxEnabled, sendHorizonPaginationHeight]);

  const onProductClick = useCallback(
    (product: Product, index: number, scrollToVariants?: boolean) => {
      const analyticsProps: CarouselPDPClickEventProps = {
        ...getSingleProductAnalytics(product),
        query: query ?? '',
        carouselName: (carouselModel?.analyticsName as string) ?? title,
        carouselIndex: (carouselModel?.carouselIndex as number) ?? 0,
        positionOnPage: (carouselModel?.carouselIndex as number) ?? 0,
        renderSize: productCardVariant,
        algorithm: data?.algorithm,
        personalised: data?.personalised,
        getPersonalizedRecommendationsFromDb: data?.recommendationType ?? '',
      };
      SendBrowserEvent.onProductClick(
        { ...product, position: index },
        analyticsProps,
        isFromBasket,
        scrollToVariants,
      );
    },
    [query, carouselModel, title, productCardVariant, data, isFromBasket],
  );

  const handleCarouselScroll = useCallback(
    (scrollDirection?: string) => {
      const sourceEvent =
        SOURCE_EVENT_MAP[recommendationType ?? ''] || SOURCE_EVENTS.HOME_PAGE_TOP_PICKS_RECOMMENDATIONS;
      const analyticParams = {
        ...analyticProperties,
        sourceEvent,
        carouselName: (carouselModel?.analyticsName as string) ?? title,
        algorithm: data?.algorithm,
        renderSize: productCardVariant,
      };
      if (!isReactNativeWebView() && scrollDirection) {
        analyticParams.scrollDirection = scrollDirection;
      }

      SendBrowserEvent.triggerCarouselScroll(analyticParams, scrollDirection, isFromBasket);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carouselModel, title, productCardVariant, data, recommendationType, query],
  );

  const getBannerAnalyticsPayload = useCallback(
    (banner: RecommendationBanner, index: number) => ({
      id: `${banner.name ?? banner.title ?? 'recommendation-banner'}-${index}`,
      title: banner.title ?? banner.name ?? '',
      imageUrl: banner.bannerUrl,
      targetType: banner.targetType,
      bannerPosition: index,
      sourceEvent: SOURCE_EVENTS.RECOMMENDATION_OI_BANNER,
      bannerType: BANNER_TYPES.TOP_BANNERS,
      ...(banner.pageHandle && { pageHandle: banner.pageHandle }),
      ...(banner.collectionId && { collectionId: String(banner.collectionId) }),
      ...(banner.name && { collectionName: banner.name }),
    }),
    [],
  );

  const trackBannerImpression = useCallback(
    (banner: RecommendationBanner, index: number) => {
      SendBrowserEvent.triggerRecommendationBannerAnalytics(BANNER_EVENTS.IMPRESSION, {
        ...getBannerAnalyticsPayload(banner, index),
        channel: ANALYTICS_CHANNELS.MKP_APP,
      });
    },
    [getBannerAnalyticsPayload],
  );

  const trackBannerSlide = useCallback(
    (banner: RecommendationBanner, index: number) => {
      SendBrowserEvent.triggerRecommendationBannerAnalytics(
        BANNER_EVENTS.SLIDE,
        getBannerAnalyticsPayload(banner, index),
      );
    },
    [getBannerAnalyticsPayload],
  );

  const handleBannerPress = useCallback(
    (banner: RecommendationBanner, index: number) => {
      SendBrowserEvent.triggerRecommendationBannerAnalytics(BANNER_EVENTS.CLICK, {
        ...getBannerAnalyticsPayload(banner, index),
        channel: ANALYTICS_CHANNELS.MKP_APP,
      });

      if (banner.targetType === 'FLEXI_PAGE') {
        if (!banner.pageHandle) return;

        SendBrowserEvent.navigateRecommendationBanner(banner);
        return;
      }

      if (banner.targetType === 'COLLECTION') {
        if (!banner.name || !banner.collectionId) return;

        SendBrowserEvent.navigateRecommendationBanner({
          ...banner,
          collectionId: String(banner.collectionId),
        });
      }
    },
    [getBannerAnalyticsPayload],
  );

  const SLIDES = useMemo(() => {
    let slides: React.ReactElement[] = [];

    if (!data || data?.products?.length === 0) {
      return slides;
    }

    if (recommendationBanners.length > 0) {
      slides.push(
        <RecommendationBannerCarousel
          key={RECOMMENDATION_BANNER_SLIDE_TYPE}
          slideType={RECOMMENDATION_BANNER_SLIDE_TYPE}
          banners={recommendationBanners}
          onBannerPress={handleBannerPress}
          onBannerImpression={trackBannerImpression}
          onBannerSlide={trackBannerSlide}
        />,
      );
    }

    slides.push(
      ...data.products.map((product: Product, index) => {
        const isPartial = !product.sku;

        return (
          <ProductContainer
            key={product.id}
            productCardVariant={productCardVariant ?? ProductCardVariant.COMPACT}
            isHorizonVertical={showVerticalList}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            {isPartial ? (
              isHorizonUiUxEnabled && isNative ? (
                <ProductCardSkeletonV3 />
              ) : (
                <SkeletonCard
                  productVariant={productCardVariant ?? ProductCardVariant.COMPACT}
                  verticalList={showVerticalList}
                />
              )
            ) : (
              <ProductCard
                product={product}
                type={productCardVariant ?? ProductCardVariant.COMPACT}
                locale={i18n.resolvedLanguage || i18n.language}
                itemIndex={index}
                autoPlayVideo={isVideoEnabled && index === firstVideoProductIndex}
                isLoggedIn={isLoggedIn as boolean}
                carouselAnalyticsProps={analyticProperties}
                onCardClick={(scrollToVariants?: boolean) => {
                  onProductClick(product, index, scrollToVariants);
                }}
                redirectToLogin={() => {
                  SendBrowserEvent.triggerRedirectToLogin(isFromBasket);
                }}
                isFromBasket={isFromBasket}
                productCardV3={isHorizonUiUxEnabled}
                onVariantModalClose={scheduleHorizonPaginationHeight}
                onAskToLoginOpen={isFromBasket && isHorizonUiUxEnabled ? handleAskToLoginOpen : undefined}
                onAskToLoginClose={handleAskToLoginClose}
              />
            )}
            {!isPartial && recommendationType === CAROUSEL_TYPES.ADS_CAROUSEL && product?.isSponsored && (
              <StyledAdBanner className="isSponsoredProduct">{t('search.ad')}</StyledAdBanner>
            )}
          </ProductContainer>
        );
      }),
    );

    if (styleConfig.showSeeAllCard && showSeeAll && !showVerticalList) {
      slides.push(<SeeAllCard handleClick={() => seeAll(SEARCH_PERFORM_TRIGGER_TYPE.SEE_ALL_CARD)} />);
    }

    return slides;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    styleConfig,
    recommendationType,
    onProductClick,
    productCardVariant,
    seeAll,
    showSeeAll,
    t,
    isLoggedIn,
    recommendationBanners,
    handleBannerPress,
    trackBannerImpression,
    trackBannerSlide,
    isHorizonUiUxEnabled,
    isVideoEnabled,
    firstVideoProductIndex,
    scheduleHorizonPaginationHeight,
    handleAskToLoginOpen,
    handleAskToLoginClose,
  ]);

  const carouselHeader: CarouselHeaderProps = useMemo(() => {
    const isBasketRecommendation = recommendationType === CAROUSEL_TYPES.BASKET_RECOMMENDATION;
    type TitleFontConfig = {
      fontTitleSize: number;
      fontTitleWeight: number;
      fontFamily?: string;
      fontStyle?: string;
      lineHeight?: string;
      fontColor?: string;
      titleDisplay?: string;
      titleAlignItems?: string;
      titleHeight?: string;
    };

    const getIcon = () => {
      if (titleIcon) {
        if (recommendationType === CAROUSEL_TYPES.SEARCH_START_TOP_PICKS) {
          return <SearchTopPicksIconNew country={countryCode as COUNTRY} />;
        } else if (isBasketRecommendation && !isHorizonUiUxEnabled) {
          return <ThumbsUpIcon country={countryCode as COUNTRY} />;
        }
      }
      return undefined;
    };

    const getFontConfig = (): TitleFontConfig => {
      if (isHorizonUiUxEnabled && isBasketRecommendation) {
        return {
          fontTitleSize: styleConfig.fontStyle.fontTitleSizeRevamp ?? styleConfig.fontStyle.fontTitleSize,
          fontTitleWeight: styleConfig.fontStyle.fontTitleWeightRevamp ?? styleConfig.fontStyle.fontTitleWeight,
          fontFamily: styleConfig.fontStyle.fontFamilyRevamp,
          fontStyle: styleConfig.fontStyle.fontStyleRevamp,
          lineHeight: styleConfig.fontStyle.lineHeightRevamp,
          fontColor: styleConfig.fontStyle.fontColorRevamp,
          titleDisplay: styleConfig.fontStyle.titleDisplayRevamp,
          titleAlignItems: styleConfig.fontStyle.titleAlignItemsRevamp,
          titleHeight: styleConfig.fontStyle.titleHeightRevamp,
        };
      } else {
        return {
          fontTitleSize: styleConfig.fontStyle.fontTitleSize,
          fontTitleWeight: styleConfig.fontStyle.fontTitleWeight,
        };
      }
    };
    const fontConfig = getFontConfig();

    return {
      title: (
        <TitleContainer isHorizonLayout={isHorizonUiUxEnabled}>
          {getIcon()}
          <TitleText
            fontSize={fontConfig.fontTitleSize}
            fontWeight={fontConfig.fontTitleWeight}
            fontFamily={fontConfig.fontFamily}
            fontStyle={fontConfig.fontStyle}
            lineHeight={fontConfig.lineHeight}
            fontColor={fontConfig.fontColor}
            titleDisplay={fontConfig.titleDisplay}
            titleAlignItems={fontConfig.titleAlignItems}
            titleHeight={fontConfig.titleHeight}
          >
            {title}
          </TitleText>
        </TitleContainer>
      ),
      children: styleConfig.showSeeAllButton && showSeeAll && !isHorizonUiUxEnabled && (
        <SeeAllButton
          endIcon={<ChevronRightSmIcon />}
          onClick={() => seeAll(SEARCH_PERFORM_TRIGGER_TYPE.SEE_ALL)}
          platform={platform}
          fontSize={styleConfig.fontStyle.fontContentSize}
          fontWeight={styleConfig.fontStyle.fontContentWeight}
          showArrow={styleConfig.showArrow}
        >
          {t('carousel.seeAll')}
        </SeeAllButton>
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    titleIcon,
    recommendationType,
    title,
    styleConfig,
    showSeeAll,
    seeAll,
    productCardVariant,
    t,
    isHorizonUiUxEnabled,
  ]);

  if (!data || data?.products?.length === 0) {
    return null;
  }

  return (
    <CarouselContainer
      ref={containerRef}
      platform={platform}
      showArrow={styleConfig.showArrow}
      isVerticalGrid={showVerticalList}
      className={recommendationType === CAROUSEL_TYPES.SEARCH_START_TOP_PICKS ? 'searchTopPicksApp' : ''}
    >
      {showVerticalList ? (
        <ProductListCarousel
          resetScrollKey={appMode}
          slides={SLIDES}
          title={title ?? ''}
          header={carouselHeader}
          isHorizonLayout={isHorizonUiUxEnabled}
          hasMore={hasProductMore}
          fetchMoreData={fetchMoreData}
          onCarouselScroll={handleCarouselScroll}
          onHeightChange={sendHorizonPaginationHeight}
          shouldFetch={shouldFetch}
          isLoading={isPaginationLoading}
        />
      ) : isTwoRowHorizontalScroll ? (
        <ProducHorizontaltListCarousel
          resetScrollKey={`${appMode}-${recommendationType}-${query}`}
          slides={SLIDES}
          title={title}
          showHeader={!isBuyAgain}
          header={carouselHeader}
          isHorizonLayout={isHorizonUiUxEnabled}
          hasMore={hasProductMore}
          fetchMoreData={fetchMoreData}
          onCarouselScroll={handleCarouselScroll}
          onHeightChange={sendHorizonPaginationHeight}
          shouldFetch={shouldFetch}
          isLoading={isPaginationLoading}
        />
      ) : (
        <ProductCarousel
          ref={scrollRef}
          header={carouselHeader}
          slides={SLIDES}
          options={{ align: 'start', dragFree: true }}
          settings={{
            gap: styleConfig.gap,
            slidesPerView: styleConfig.slidesPerView,
            showArrow: styleConfig.showArrow,
            responsive: styleConfig.responsive,
          }}
          onCarouselScroll={handleCarouselScroll}
          scrollAnalyticsDebounce={300}
          productSize={productCardVariant}
          recommendationType={recommendationType}
          showSeeAll={showSeeAll}
        />
      )}
      {isAskToLoginOpen && <BottomSheetBackdrop aria-hidden="true" />}
    </CarouselContainer>
  );
};
